import { useEffect } from 'react';
import * as signalR from '@microsoft/signalr';
import { apiVariables } from '../../services/api/apiVariables';
import { refreshNotifications, setUnreadNotificationsCount } from '../../redux/slices/userProfileSlice';
import { useAppDispatch, useAppSelector } from '../../store';
import { getSensitiveData } from '../../utils/secureStorage';

/** Keeps notification counts and open notification views in sync with SignalR. */
export default function NotificationStateSync() {
  const dispatch = useAppDispatch();
  const authToken = useAppSelector((state) => state.userProfile.authToken);

  useEffect(() => {
    let isSubscribed = true;
    let connection: signalR.HubConnection | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = async () => {
      const token = authToken || await getSensitiveData('authToken');
      if (!token || !isSubscribed) return;

      connection = new signalR.HubConnectionBuilder()
        .withUrl(apiVariables.NOTIFICATION_HUB_URL, {
          withCredentials: true,
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
        })
        .withAutomaticReconnect({ nextRetryDelayInMilliseconds: () => 5000 })
        .configureLogging(signalR.LogLevel.None)
        .build();

      connection.on('ReceiveNotificationCount', (notificationCount: unknown) => {
        if (!isSubscribed || typeof notificationCount !== 'number') return;

        dispatch(setUnreadNotificationsCount(notificationCount));
        dispatch(refreshNotifications());
      });

      connection.onreconnected(() => {
        if (isSubscribed) dispatch(refreshNotifications());
      });

      try {
        await connection.start();
      } catch (error) {
        console.error('Notification SignalR connection failed:', error);
        if (isSubscribed) retryTimer = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      isSubscribed = false;
      if (retryTimer) clearTimeout(retryTimer);
      connection?.stop().catch((error) => console.error('Failed to stop notification SignalR connection:', error));
    };
  }, [authToken, dispatch]);

  return null;
}
